import {
  MacroPackageMessageType, macroPackageRequestSchema, macroRuntimeModeSchema,
  type LocalEditorEvent, type LocalEditorRequest, type LocalHostDescriptor, type MacroPackageCaptureRequest,
  type PlaycastMessage, type PlaycastMessageTarget,
} from '@playcastdotio/Messaging';
import type { MacroSessionCoordinator } from '../macro/MacroSessionCoordinator';
import { MacroPackageCaptureRelay, type RawPackageUploadClient } from './MacroPackageCaptureRelay';
import type { IIpcService } from '../../services/ipc/IIpcService';
import type { IPlayjectorService } from '../../services/playjector/interfaces/IPlayjectorService';
import type { RecordingControlManager } from '../../services/playjector/managers/RecordingControlManager';
import type { PeerCoordinator } from '../peer/PeerCoordinator';
import type { LocalHostApiRendererService, LocalHostCampaignPublishIpcFrame, LocalHostRendererFrame } from '../../services/local-host-api/LocalHostApiRendererService';
import type { MacroRecorderCaptureSourceManager } from '../../services/playjector/managers/MacroRecorderCaptureSourceManager';
import type { MacroVisualTestManager } from '../../services/playjector/managers/MacroVisualTestManager';
import type { ReferenceCaptureManager } from '../../services/playjector/managers/ReferenceCaptureManager';
import type { ReferenceCapturePushResult } from '../../services/playjector/referenceCapture/NativeReferenceCaptureClient';
import { MacroInputCaptureRelay } from './MacroInputCaptureRelay';
import { LocalVisualTestCommandHandler } from './LocalVisualTestCommandHandler';
import { LocalReferenceCaptureCommandHandler } from './LocalReferenceCaptureCommandHandler';
import type { LocalSourceGraphActionRouter } from './LocalSourceGraphActionRouter';
import type { NativeMacroPackageBridge } from '../../services/playjector/NativeMacroPackageBridge';
import { macroPackageCapabilities } from './macroPackageCapabilities';
import { LOCAL_OWNED_NATIVE_ACTION, type LocalOwnedNativeActionRouter } from './LocalOwnedNativeActionRouter';
import { RemoteRawPackageTransferManager } from './RemoteRawPackageTransferManager';
import { RemoteRecordingCatalogManager } from './RemoteRecordingCatalogManager';
import { LocalHostPeerBoundary } from './LocalHostPeerBoundary';
import { LocalHostVideoSignalHandler } from './LocalHostVideoSignalHandler';

export interface LocalHostIdentity {
  machineId(): string;
  branch: string;
  version: string;
  realtimeConnected(): boolean;
}

interface ActiveSession { clientId: string; sessionId: string; peerId: string; transportPeerId: string; generation: number }

/**
 * Compiled-package wiring. `capture` is the host's single raw-package capture relay; `claims` marks main-process
 * traffic the macro-package IPC router already delivers, so it is not echoed to the local editor.
 */
export interface LocalHostPackageRuns {
  bridge?: NativeMacroPackageBridge;
  ownedNative?: LocalOwnedNativeActionRouter;
  sourceGraphs?: LocalSourceGraphActionRouter;
  sessions?: Pick<MacroSessionCoordinator, 'start' | 'validatePackage' | 'cancel' | 'sessions'>;
  uploader?: RawPackageUploadClient;
  capture?: MacroPackageCaptureRelay;
  claims?: (message: PlaycastMessage<PlaycastMessageTarget>) => boolean;
}
const NO_UPLOADER: RawPackageUploadClient = { upload: async () => ({ ok: false, error: 'raw_package_upload_unavailable' }) };
/** Editor → host preview run: the Phase 1 `MACROPACKAGE_REQUEST` shape inside the local-editor envelope. */
const PREVIEW_ACTIONS = new Set(['macroPackagePreview', 'macroPackageRequest', MacroPackageMessageType.REQUEST]);

export class LocalHostSessionCoordinator {
  private active: ActiveSession | null = null;
  private initialized = false;
  private readonly inputCapture: MacroInputCaptureRelay;
  private readonly packageCapture: MacroPackageCaptureRelay;
  private readonly visualTestCommands: LocalVisualTestCommandHandler;
  private readonly referenceCaptureCommands: LocalReferenceCaptureCommandHandler;
  private packageCaptureOwner: string | null = null;
  private readonly remoteTransfers: RemoteRawPackageTransferManager;
  private readonly remoteCatalog: RemoteRecordingCatalogManager;
  private readonly videoSignaling: LocalHostVideoSignalHandler;
  private readonly localPeers: LocalHostPeerBoundary;

  public constructor(
    private readonly renderer: LocalHostApiRendererService,
    private readonly peer: PeerCoordinator,
    private readonly ipc: IIpcService,
    private readonly playjector: IPlayjectorService,
    private readonly recorder: RecordingControlManager,
    private readonly identity: LocalHostIdentity,
    private readonly captureSources?: MacroRecorderCaptureSourceManager,
    private readonly visualTests?: MacroVisualTestManager,
    private readonly referenceCapture?: ReferenceCaptureManager,
    private readonly packageRuns: LocalHostPackageRuns = {},
  ) {
    this.videoSignaling = new LocalHostVideoSignalHandler(peer, renderer);
    this.localPeers = new LocalHostPeerBoundary(peer, this);
    this.inputCapture = new MacroInputCaptureRelay(ipc, (peerId, raw) => {
      if (this.owns(peerId)) this.sendMessageToBrowser(peerId, raw);
      else this.peer.sendToPeer(peerId, raw);
    });
    this.packageCapture = packageRuns.capture
      ?? new MacroPackageCaptureRelay({ ipc, uploader: packageRuns.uploader ?? NO_UPLOADER, mode: () => 'local' });
    this.remoteTransfers = new RemoteRawPackageTransferManager(packageRuns.uploader ?? NO_UPLOADER, () => this.identity.machineId());
    this.remoteCatalog = new RemoteRecordingCatalogManager(packageRuns.uploader ?? NO_UPLOADER, () => this.identity.machineId());
    this.referenceCaptureCommands = new LocalReferenceCaptureCommandHandler(referenceCapture, {
      reply: (peerId, action, result) => { void this.reply(peerId, action, result); },
    });
    this.visualTestCommands = new LocalVisualTestCommandHandler(visualTests, {
      reply: (peerId, action, result) => { void this.reply(peerId, action, result); },
      progress: (peerId, requestId, progress) => { this.sendMessageToBrowser(peerId, JSON.stringify({
        header: { target: 'system', action: 'macroRecorderVisualTestProgress', isReply: false, source: 'host' },
        body: { message: { requestId, progress } },
      })); },
    });
  }

  public init(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.renderer.subscribe((frame) => { void this.handleFrame(frame); });
    this.renderer.subscribePublish?.((frame) => { void this.handleCampaignPublish(frame); });
  }
  public owns(peerId: string): boolean { return this.active?.peerId === peerId || this.active?.transportPeerId === peerId; }
  public ownsVideoPeer(peerId: string): boolean { return this.active?.transportPeerId === peerId; }
  public ownsSession(sessionId: string): boolean { return this.active?.sessionId === sessionId; }
  /** Remote callers have already passed AdminPeerHandler's peer ownership check. */
  public handleRecorderInput(peerId: string, payload: unknown): void { this.inputCapture.handle(peerId, payload, this.owns(peerId)); }
  public handlePackageCapabilities(payload: unknown): Promise<Record<string, unknown>> { return macroPackageCapabilities(payload, this.packageRuns.bridge); }
  public releaseRecorderInput(peerId: string): void { this.inputCapture.release(peerId); this.releasePackageCapture(peerId); this.remoteTransfers.release(peerId); this.remoteCatalog.release(peerId); }
  public onConnected(peerId: string): void {
    if (LocalHostPeerBoundary.isLocalPeer(peerId) && !this.ownsVideoPeer(peerId)) this.peer.disconnectLocalPeer(peerId);
  }
  public onDisconnected(peerId: string): void {
    if (!this.ownsVideoPeer(peerId)) return;
    this.inputCapture.release(peerId);
    this.releasePackageCapture(peerId);
    // The WebSocket owns this local session, not the replaceable media peer.
    // Losing video must not destroy calibrated tests or cancel the native matcher.
  }

  public handlePeerMessage(peerId: string, message: PlaycastMessage<PlaycastMessageTarget>): boolean {
    return this.localPeers.message(peerId, () => { this.handleMessage(peerId, message); });
  }
  public handleMessage(peerId: string, message: PlaycastMessage<PlaycastMessageTarget>): boolean {
    if (!this.owns(peerId)) return false;
    peerId = this.active!.peerId;
    const action = String(message.header.action);
    if (action === LOCAL_OWNED_NATIVE_ACTION) return true; // Consume browser attempts; only trusted main IPC dispatches this action.
    const payload = (message.body.message ?? {}) as Record<string, unknown>;
    if (action === 'macroPackageCapabilities') { void this.reply(peerId, action, this.handlePackageCapabilities(payload)); return true; }
    if (this.packageRuns.sourceGraphs?.dispatch(action, payload, (name, value) => {
      void this.reply(peerId, name, Promise.resolve(value));
    }, value => this.sendMessageToBrowser(peerId, JSON.stringify({ header: { target: 'system',
      action: 'macroLocalGraphProgress', isReply: false, source: 'host' }, body: { message: value } })))) return true;
    if (action === 'macroRecorderInputCapture') { this.handleRecorderInput(peerId, payload); return true; }
    if (this.dispatchPackageAction(peerId, action, payload)) return true;
    if (action === 'macroRecorderKeepalive') return true;
    if (action === 'macroRecorderScreenshot') {
      void this.reply(peerId, action, this.recorder.handleMacroRecorderScreenshot(payload as never)); return true;
    }
    if (action === 'macroRecorderRecordControl') {
      void this.reply(peerId, action, this.recorder.handleRecordControl(payload as never)); return true;
    }
    if (action === 'macroRecorderUploadScreenshots') {
      void this.reply(peerId, action, this.recorder.handleUploadScreenshots(payload as never)); return true;
    }
    if (action === 'macroRecorderCaptureSources' && this.captureSources) {
      const requestId = String(payload.requestId ?? '');
      const result = this.captureSources.listSources(payload.captureElementName as string | undefined)
        .then((sources) => ({ requestId, success: true, ...sources }))
        .catch((error) => ({
          requestId, success: false, sources: [], selectedId: '',
          error: error instanceof Error ? error.message : 'capture_source_list_failed',
        }));
      void this.reply(peerId, action, result);
      return true;
    }
    if (action === 'macroRecorderCaptureSourceSelect' && this.captureSources) {
      const requestId = String(payload.requestId ?? '');
      const result = this.captureSources.selectSource(payload.source as never)
        .then((selectedId) => ({ requestId, success: true, selectedId }))
        .catch((error) => ({
          requestId, success: false,
          error: error instanceof Error ? error.message : 'capture_source_select_failed',
        }));
      void this.reply(peerId, action, result);
      return true;
    }
    if (this.visualTestCommands.dispatch(peerId, action, payload)) return true;
    if (this.referenceCaptureCommands.dispatch(peerId, action, payload)) return true;
    this.ipc.send(message);
    return true;
  }

  public forwardPlayjectorMessage(message: PlaycastMessage<PlaycastMessageTarget>): void {
    if (this.inputCapture.receive(message)) return;
    if (this.packageCapture.receive(message)) return;
    if (this.packageRuns.claims?.(message)) return;
    this.captureSources?.handlePlayjectorMessage(message);
    if (this.active) this.sendMessageToBrowser(this.active.peerId, JSON.stringify(message));
  }

  public sendRecorderStatus(status: Record<string, unknown>): void {
    if (!this.active) return;
    this.sendMessageToBrowser(this.active.peerId, JSON.stringify({
      header: { target: 'system', action: 'macroRecorderUploadStatus', isReply: false, source: 'host' },
      body: { message: status },
    }));
  }

  private async handleFrame(frame: LocalHostRendererFrame): Promise<void> {
    if ('disconnected' in frame) { if (this.active?.clientId === frame.clientId) this.disconnect(); return; }
    if ('publish' in frame) return;
    const { clientId, request, playjectorConnected } = frame;
    try {
      if (request.op === 'discover') { this.renderer.send(clientId, { v: 1, id: request.id, event: 'discovered', host: this.descriptor(playjectorConnected) }); return; }
      if (request.op === 'connect') { this.connect(clientId, request, playjectorConnected); return; }
      if (!this.active || this.active.clientId !== clientId || request.sessionId !== this.active.sessionId) throw new Error('session_not_owned');
      if (request.op === 'signal') {
        const active = this.active;
        await this.videoSignaling.handle(active, request, () => this.active === active);
      } else if (request.op === 'reconnectVideo') {
        const previousPeerId = this.active.transportPeerId;
        const generation = ++this.active.generation;
        this.active.transportPeerId = `${this.active.peerId}:video:${generation}`;
        // Rotate identity before closing: late disconnect/SDP callbacks cannot affect the replacement.
        this.peer.disconnectLocalPeer(previousPeerId);
        this.renderer.send(clientId, { v: 1, id: request.id, event: 'videoReset', sessionId: request.sessionId, generation });
      } else if (request.op === 'message') {
        if (String(request.message.header.action) === LOCAL_OWNED_NATIVE_ACTION) {
          if (!frame.serverOwned || !this.packageRuns.ownedNative) throw new Error('session_not_owned');
          const payload = request.message.body.message as { command?: unknown };
          const active = this.active;
          this.packageRuns.ownedNative.dispatch(payload.command, reply => active === this.active && this.sendMessageToBrowser(active.peerId, JSON.stringify({
            header: { target: 'system', action: LOCAL_OWNED_NATIVE_ACTION, isReply: true, source: 'host' }, body: { message: { reply } },
          })));
        } else this.handleMessage(this.active.peerId, request.message as PlaycastMessage<PlaycastMessageTarget>);
      }
      else if (request.op === 'disconnect') this.disconnect();
    } catch (error) {
      this.error(clientId, request.id, error instanceof Error ? error.message : 'local_host_error');
    }
  }

  private connect(clientId: string, request: Extract<LocalEditorRequest, { op: 'connect' }>, playjectorConnected: boolean): void {
    if (this.active) throw new Error('local_host_busy');
    if (!this.playjector.isAvailable() || !playjectorConnected) throw new Error('local_playjector_unavailable');
    const sessionId = crypto.randomUUID();
    const peerId = `local-editor:${sessionId}`;
    this.active = { clientId, sessionId, peerId, transportPeerId: peerId, generation: 0 };
    this.renderer.send(clientId, { v: 1, id: request.id, event: 'connected', host: { kind: 'local', machineId: this.identity.machineId(), sessionId } });
  }

  private disconnect(): void {
    const active = this.active; if (!active) return; this.active = null;
    this.inputCapture.release(active.peerId);
    this.releasePackageCapture(active.peerId);
    this.visualTests?.release();
    this.packageRuns.sourceGraphs?.release();
    void this.packageRuns.ownedNative?.release().catch(() => undefined);
    this.referenceCapture?.release();
    this.peer.disconnectLocalPeer(active.transportPeerId);
    this.renderer.send(active.clientId, { v: 1, event: 'disconnected', sessionId: active.sessionId, reason: 'client_disconnect' });
  }

  private descriptor(playjectorConnected: boolean): LocalHostDescriptor {
    const machineId = this.identity.machineId();
    return { machineId, label: machineId || 'Local Host', branch: this.identity.branch, hostVersion: this.identity.version,
      protocolVersion: 1, playjectorConnected: this.playjector.isAvailable() && playjectorConnected,
      realtimeConnected: this.identity.realtimeConnected(), videoReconnectSupported: true };
  }

  public pushReferenceCaptureSample(sessionId: string, result: ReferenceCapturePushResult): void {
    if (!this.active) return;
    this.sendMessageToBrowser(this.active.peerId, JSON.stringify({
      header: { target: 'system', action: 'macroRecorderReferenceCaptureSample', isReply: false, source: 'host' },
      body: { message: { sessionId, result } },
    }));
  }

  /**
   * Raw package capture for the local editor or a remote admin peer (already past AdminPeerHandler's
   * admin-session check). One recording per host: another peer is refused until the owner finishes.
   */
  public handlePackageCapture(peerId: string, payload: unknown): void {
    const local = this.owns(peerId);
    const respond = (reply: unknown) => this.sendPackageCaptureReply(peerId, local, reply);
    const request = (payload ?? {}) as Partial<MacroPackageCaptureRequest>;
    if (this.packageCaptureOwner && this.packageCaptureOwner !== peerId && this.packageCapture.owned) {
      respond({ requestId: String(request.requestId ?? ''), packageId: String(request.packageId ?? ''), success: false,
        events: [], more: false, stopped: true, error: 'input_capture_busy' });
      return;
    }
    const reply = this.packageCapture.handle(request as MacroPackageCaptureRequest, local ? 'local' : 'campaign');
    if (this.packageCapture.owned) this.packageCaptureOwner = peerId;
    void reply.then((message) => {
      if (!this.packageCapture.owned && this.packageCaptureOwner === peerId) this.packageCaptureOwner = null;
      respond(message);
    });
  }

  /** Called only after AdminPeerHandler authorizes the current remote admin peer. */
  public handlePackageUpload(peerId: string, payload: unknown): unknown {
    if (this.owns(peerId)) return null;
    return this.remoteTransfers.handle(peerId, payload);
  }
  public handleRecordingCatalog(peerId: string, payload: unknown): Promise<unknown> {
    return this.owns(peerId) ? Promise.resolve(null) : this.remoteCatalog.handle(peerId, payload);
  }

  private releasePackageCapture(peerId: string): void {
    if (this.packageCaptureOwner !== peerId) return;
    this.packageCaptureOwner = null;
    this.packageCapture.release();
  }

  private sendPackageCaptureReply(peerId: string, local: boolean, message: unknown): void {
    const raw = JSON.stringify({ header: { target: 'system', action: 'macroPackageCapture', isReply: true, source: 'host' }, body: { message } });
    if (local) this.sendMessageToBrowser(peerId, raw);
    else this.peer.sendToPeer(peerId, raw);
  }

  /** Preview runs and the session list/cancel for the owning local editor. */
  private dispatchPackageAction(peerId: string, action: string, payload: Record<string, unknown>): boolean {
    if (action === 'macroPackageCapture') { this.handlePackageCapture(peerId, payload); return true; }
    const isPreview = PREVIEW_ACTIONS.has(action) || payload.type === MacroPackageMessageType.REQUEST;
    if (!isPreview && action !== 'macroPackageSessions' && action !== 'macroPackageCancel' && action !== 'macroPackageValidate') return false;
    const requestId = String(payload.requestId ?? '');
    const sessions = this.packageRuns.sessions;
    if (!sessions) { void this.reply(peerId, action, Promise.resolve({ requestId, success: false, error: 'macro_sessions_unavailable' })); return true; }
    if (action === 'macroPackageSessions') {
      void this.reply(peerId, action, Promise.resolve({ requestId, success: true, sessions: sessions.sessions() }));
    } else if (action === 'macroPackageValidate') {
      const result = sessions.validatePackage({ campaignId: String(payload.campaignId ?? ''), trigger: String(payload.trigger ?? ''),
        packageSha256: String(payload.packageSha256 ?? '') }).then(validation => ({ requestId, success: validation.ok,
          ...(!validation.ok ? { error: validation.error } : {}) }));
      void this.reply(peerId, action, result);
    } else if (action === 'macroPackageCancel') {
      void this.reply(peerId, action, sessions.cancel(String(payload.runId ?? '')).then(() => ({ requestId, success: true })));
    } else {
      void this.reply(peerId, action, this.startPreview(sessions, payload));
    }
    return true;
  }

  /** Verifies the selected Playjector can load a compiled campaign package without executing it. */
  public async handlePackageValidation(peerId: string, payload: unknown): Promise<Record<string, unknown>> {
    const body = (payload ?? {}) as Record<string, unknown>;
    const requestId = String(body.requestId ?? '');
    const sessions = this.packageRuns.sessions;
    if (!sessions) return { requestId, success: false, error: 'macro_sessions_unavailable' };
    const result = await sessions.validatePackage({ campaignId: String(body.campaignId ?? ''), trigger: String(body.trigger ?? ''),
      packageSha256: String(body.packageSha256 ?? '') });
    return result.ok ? { requestId, success: true } : { requestId, success: false, error: result.error };
  }

  private async handleCampaignPublish(frame: LocalHostCampaignPublishIpcFrame): Promise<void> {
    const { clientId, request } = frame;
    const active = this.active;
    const reply = (success: boolean, error?: string) => this.renderer.sendPublishResult({ requestId: frame.requestId,
      clientId, sessionId: frame.sessionId, success, ...(error ? { error: error.slice(0, 256) } : {}) });
    if (!active || active.clientId !== clientId || active.sessionId !== frame.sessionId) { reply(false, 'session_not_owned'); return; }
    const uploader = this.packageRuns.uploader ?? NO_UPLOADER;
    for (const item of frame.packages) {
      try {
        const result = await uploader.upload(item.packageId, request.campaignId, request.uploadGrantToken);
        if (!result.ok) { reply(false, result.error); return; }
      } catch (error) {
        reply(false, error instanceof Error ? error.message : 'raw_package_upload_failed'); return;
      }
    }
    reply(true);
  }

  private async startPreview(sessions: NonNullable<LocalHostPackageRuns['sessions']>, payload: Record<string, unknown>): Promise<unknown> {
    // The editor's requestId correlates the local reply; the loader mints the realtime requestId.
    const { mode: requestedMode, requestId: editorRequestId, ...request } = payload;
    const parsed = macroPackageRequestSchema.safeParse({ id: crypto.randomUUID(), timestamp: Date.now(), ...request,
      requestId: crypto.randomUUID(), type: MacroPackageMessageType.REQUEST, variant: 'preview' });
    const requestId = String(editorRequestId ?? '');
    if (!parsed.success) return { requestId, success: false, error: 'invalid_command' };
    const mode = macroRuntimeModeSchema.safeParse(requestedMode);
    const session = await sessions.start({ campaignId: parsed.data.campaignId, trigger: parsed.data.trigger, variant: 'preview',
      packageSha256: parsed.data.packageSha256, mode: mode.success ? mode.data : 'local-calibrated' });
    return { requestId, success: session.state !== 'failed', session, ...(session.errorCode ? { error: session.errorCode } : {}) };
  }

  private async reply(peerId: string, action: string, result: Promise<unknown>): Promise<void> {
    try {
      const message = await result;
      const sent = this.sendMessageToBrowser(peerId, JSON.stringify({ header: { target: 'system', action, isReply: true, source: 'host' }, body: { message } }));
      if (!sent) console.warn(`[LocalHostSessionCoordinator] Reply send failed for ${action}`);
    } catch (error) {
      const sent = this.sendMessageToBrowser(peerId, JSON.stringify({ header: { target: 'system', action, isReply: true, source: 'host' },
        body: { message: { success: false, error: error instanceof Error ? error.message : 'local_recorder_error' } } }));
      if (!sent) console.warn(`[LocalHostSessionCoordinator] Error reply send failed for ${action}`);
    }
  }
  private sendMessageToBrowser(peerId: string, raw: string): boolean {
    if (!this.active || this.active.peerId !== peerId) return false;
    this.renderer.send(this.active.clientId, {
      v: 1, event: 'message', sessionId: this.active.sessionId, raw,
    });
    return true;
  }
  private error(clientId: string, id: string, code: string): void {
    const event: LocalEditorEvent = { v: 1, id, event: 'error', code, message: code };
    this.renderer.send(clientId, event);
  }
}
